import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { storeZohoTokens } from "../../../../shared/data/storage/authStorage";
import { exchangeZohoCodeApi } from "../../data/api/authApi";

export const ZohoCallbackPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { updateZohoToken } = useAuth();
  const hasProcessedRef = useRef<boolean>(false);

  useEffect(() => {
    if (hasProcessedRef.current) {
      console.log("Callback already processed, skipping...");
      return;
    }

    hasProcessedRef.current = true;
    console.log("Processing Zoho callback...");
    handleOAuthCallback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOAuthCallback = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const errorParam = urlParams.get("error");

      if (errorParam) {
        throw new Error(`Zoho authentication failed: ${errorParam}`);
      }

      if (!code) {
        throw new Error("No authorization code received from Zoho");
      }

      // Exchange code for token using the API
      const data = await exchangeZohoCodeApi(code);
      console.log("Zoho token exchange successful:", data);

      // Store the Zoho tokens in localStorage
      storeZohoTokens(data.refreshToken);
      console.log("Zoho tokens stored");

      if (data.refreshToken) {
        const updateResult = await updateZohoToken(data.refreshToken);
        if (!updateResult.success) {
          throw new Error("Failed to update user with Zoho token");
        }
      }

      navigate("/dashboard");
    } catch (err) {
      console.error("OAuth callback error:", err);
      const error = err as Error;
      setError(`Failed to complete Zoho authentication: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-default-background">
        <div className="flex flex-col items-center gap-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
          <div className="flex flex-col items-center gap-2">
            <h2 className="text-heading-2 font-heading-2 text-default-font">
              Completing Zoho Authentication...
            </h2>
            <p className="text-body font-body text-subtext-color">
              Please wait while we complete your Zoho authentication.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-default-background">
        <div className="flex w-full max-w-md flex-col items-center gap-6 p-8">
          <div className="flex flex-col items-center gap-4">
            <h2 className="text-heading-2 font-heading-2 text-default-font">
              Authentication Error
            </h2>
            <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
              <span className="text-sm font-medium text-red-800">{error}</span>
            </div>
          </div>
          <button
            className="px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            onClick={() => navigate("/")}
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return null;
};
